/**
 * Copyright 2025 Lumavec Group LLC
 *
 * File changes list component - shows list of changed files with selection
 */

import React from 'react';
import { Loader, Check } from 'lucide-react';
import type { WorktreeStatus, FileChange, SourceControlProvider } from './types';

interface FileChangesListProps {
  status: WorktreeStatus | null;
  selectedFiles: Set<string>;
  selectedFile: string | null;
  loading: boolean;
  refreshing: boolean;
  onToggleFileSelection: (filePath: string) => void;
  onSelectAllFiles: () => void;
  onClearSelection: () => void;
  onFileClick: (filePath: string) => void;
  onShowCommitDialog: () => void;
  onShowRevertDialog: (revertAll: boolean) => void;
  onShowPRDialog: () => void;
  isSourceControlConfigured: boolean;
  sourceControlProvider: SourceControlProvider;
}

function renderStatusBadge(status: FileChange['status']) {
  const colors: Record<string, string> = {
    modified: 'bg-yellow-100 text-yellow-800',
    added: 'bg-green-100 text-green-800',
    deleted: 'bg-red-100 text-red-800',
    renamed: 'bg-blue-100 text-blue-800',
    untracked: 'bg-gray-100 text-gray-800'
  };
  
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export function FileChangesList({
  status,
  selectedFiles,
  selectedFile,
  loading,
  refreshing,
  onToggleFileSelection,
  onSelectAllFiles,
  onClearSelection,
  onFileClick,
  onShowCommitDialog,
  onShowRevertDialog,
  onShowPRDialog,
  isSourceControlConfigured,
  sourceControlProvider
}: FileChangesListProps) {
  return (
    <>
      {/* Action Buttons - Compact single row */}
      {status?.hasChanges && (
        <div className="px-3 py-2 space-y-1.5 changes-page-panel-header">
          <div className="flex items-center gap-2">
            <button
              onClick={onSelectAllFiles}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Select All
            </button>
            {selectedFiles.size > 0 && (
              <button
                onClick={onClearSelection}
                className="text-xs text-gray-600 hover:text-gray-800"
              >
                Clear ({selectedFiles.size})
              </button>
            )}
          </div>
          
          {/* All buttons in one row - compact text only */}
          <div className="flex gap-1">
            <button
              onClick={onShowCommitDialog}
              className="btn-primary-sm flex-1"
              title="Commit changes"
            >
              Commit
            </button>
            
            <button
              onClick={() => onShowRevertDialog(false)}
              disabled={selectedFiles.size === 0}
              className="btn-secondary-sm flex-1"
              title="Revert selected files"
            >
              Revert
            </button>
            
            <button
              onClick={onShowPRDialog}
              disabled={!isSourceControlConfigured}
              className="btn-secondary-sm flex-1"
              title={!isSourceControlConfigured ? `${sourceControlProvider === 'github' ? 'GitHub' : 'GitLab'} access token required` : 'Create pull request'}
            >
              {sourceControlProvider === 'github' ? 'PR' : 'MR'}
            </button>
            {/*
            <button
              onClick={() => onShowRevertDialog(true)}
              className="btn-danger-sm flex-1"
              title="Revert all changes"
            >
              Revert All
            </button>
            */}
          </div>
        </div>
      )}

      {/* File List */}
      <div className="flex-1 overflow-auto">
        {loading || refreshing ? (
          <div className="flex items-center justify-center h-32">
            <Loader size={24} className="animate-spin text-blue-600" />
          </div>
        ) : status?.files && status.files.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {status.files.map((file) => (
              <div
                key={file.path}
                className={`p-3 hover:bg-gray-50 cursor-pointer ${
                  selectedFile === file.path ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                }`}
                onClick={() => onFileClick(file.path)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-2 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedFiles.has(file.path)}
                      onChange={(e) => {
                        e.stopPropagation();
                        onToggleFileSelection(file.path);
                      }}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {file.path.split('/').pop()}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {file.path}
                      </div>
                    </div>
                  </div>
                  {renderStatusBadge(file.status)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <Check size={32} className="mx-auto text-green-500 mb-2" />
              <p>No changes</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

